// ============================================================================
// ProgressSnapshot — a vivid inline-SVG area/line chart of a client's weight
// trend, animated path draw, and a client switcher. Now backed by the real
// per-client metrics endpoint (GET /api/clients/{id}/metrics -> series.weight).
// Pure SVG (no charting dependency) so the bundle stays lean.
//
// The metrics endpoint returns measurement-derived series only; client goals
// aren't exposed by the API yet, so the goal reference line is omitted until
// that lands (graceful — the chart + delta still tell the story).
// ============================================================================
import { useEffect, useId, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Card, CardHeader, EmptyState, SelectField } from "../components";
import { IconArrowDown } from "../components/icons";
import { useClientMetrics } from "../hooks/measurements";
import { useDashboardCtx } from "./DashboardDataContext";
import { LoadingBlock } from "../app/PageState";

const W = 560;
const H = 220;
const PAD = { top: 18, right: 18, bottom: 28, left: 38 };

export function ProgressSnapshot() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const { clients } = useDashboardCtx();
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const gradId = useId();

  // Default to the first client once the roster loads.
  useEffect(() => {
    if (clientId === undefined && clients.length > 0) {
      setClientId(clients[0].id);
    }
  }, [clients, clientId]);

  const metricsQuery = useClientMetrics(clientId);
  const series = metricsQuery.data?.series.weight ?? [];
  const client = clients.find((c) => c.id === clientId);

  const chart = useMemo(() => {
    if (series.length < 2) return null;
    const pts = series.map((p) => p.y);
    const lo = Math.floor(Math.min(...pts) - 1);
    const hi = Math.ceil(Math.max(...pts) + 1);
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const range = hi - lo || 1;

    const x = (i: number) => PAD.left + (innerW * i) / (series.length - 1);
    const y = (v: number) => PAD.top + innerH * (1 - (v - lo) / range);

    const linePath = series
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.y).toFixed(1)}`)
      .join(" ");
    const areaPath = `${linePath} L ${x(series.length - 1).toFixed(1)} ${(H - PAD.bottom).toFixed(1)} L ${x(0).toFixed(1)} ${(H - PAD.bottom).toFixed(1)} Z`;
    const ticks = [lo, Math.round((lo + hi) / 2), hi];

    return {
      linePath,
      areaPath,
      ticks,
      yPos: (v: number) => y(v),
      lastX: x(series.length - 1),
      lastY: y(pts[pts.length - 1]),
    };
  }, [series]);

  return (
    <Card className="flex h-full flex-col">
      <CardHeader
        title={t("snapshot.title")}
        subtitle={t("snapshot.subtitle")}
        action={
          <div className="w-44">
            <SelectField
              label={t("snapshot.client")}
              hideLabel
              value={clientId ?? ""}
              onChange={(e) => setClientId(Number(e.target.value))}
              options={clients.map((c) => ({ value: String(c.id), label: c.name }))}
            />
          </div>
        }
      />

      {metricsQuery.isLoading ? (
        <LoadingBlock />
      ) : !chart ? (
        <EmptyState
          title={t("common.error")}
          description={t("common.errorBlurb")}
          className="flex-1 !py-10"
        />
      ) : (
        <>
          {/* Headline number */}
          <div className="mb-4 flex flex-wrap items-end gap-4">
            {client && (
              <div className="flex items-center gap-3">
                <Avatar name={client.name} size="md" />
                <div>
                  <p className="font-display text-sm font-bold text-body">{client.name}</p>
                  {client.occupation && <p className="text-xs text-body-soft">{client.occupation}</p>}
                </div>
              </div>
            )}
            <div className="ml-auto flex items-end gap-2">
              <span className="nums font-display text-4xl font-extrabold leading-none tracking-tightest text-body">
                {series[series.length - 1].y}
                <span className="ml-1 text-lg font-bold text-body-soft">kg</span>
              </span>
              <Delta first={series[0].y} last={series[series.length - 1].y} />
            </div>
          </div>

          {/* Chart */}
          <div className="relative">
            <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label={client ? t("dashboard:snapshot.chartLabelNamed", { name: client.name }) : t("dashboard:snapshot.chartLabel")}>
              <defs>
                <linearGradient id={`${gradId}-area`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF5E62" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#FF9966" stopOpacity="0" />
                </linearGradient>
                <linearGradient id={`${gradId}-line`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FF5E62" />
                  <stop offset="100%" stopColor="#FF9966" />
                </linearGradient>
              </defs>

              {chart.ticks.map((tick) => (
                <g key={tick}>
                  <line x1={PAD.left} x2={W - PAD.right} y1={chart.yPos(tick)} y2={chart.yPos(tick)} stroke="#EAEDF5" strokeWidth="1" />
                  <text x={PAD.left - 8} y={chart.yPos(tick) + 4} textAnchor="end" className="fill-body-faint text-[10px] font-bold">
                    {tick}
                  </text>
                </g>
              ))}

              <motion.path
                d={chart.areaPath}
                fill={`url(#${gradId}-area)`}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              />
              <motion.path
                d={chart.linePath}
                fill="none"
                stroke={`url(#${gradId}-line)`}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={reduce ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
              />
              <motion.circle
                cx={chart.lastX}
                cy={chart.lastY}
                r="5"
                fill="#fff"
                stroke="#FF5E62"
                strokeWidth="3"
                initial={reduce ? false : { scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: reduce ? 0 : 1.1, type: "spring", stiffness: 400, damping: 18 }}
                style={{ transformOrigin: "center", transformBox: "fill-box" } as React.CSSProperties}
              />
            </svg>
          </div>
        </>
      )}
    </Card>
  );
}

function Delta({ first, last }: { first: number; last: number }) {
  const change = +(last - first).toFixed(1);
  return (
    <Badge tone={change <= 0 ? "success" : "danger"} className="mb-1">
      <IconArrowDown width={13} height={13} className={change > 0 ? "rotate-180" : ""} />
      {change > 0 ? "+" : ""}
      {change} kg
    </Badge>
  );
}
